import React, { useState, useContext } from "react"
import PropTypes from "prop-types"
import styled from "styled-components"
import { Accordion } from "@zendeskgarden/react-accordions"
import SidebarListItem from "./sidebarListItem"
import { sidebarDataNodeShape } from "../../lib/prop-types"
import { GlobalContext } from "../../lib/context/globalContextProvider"

const ParentSectionTitle = styled.h2`
  margin: ${props => `0 ${props.theme.space.horz.sm} ${props.theme.space.lg}`};
  font-weight: ${props => props.theme.fontWeights.semibold};
  font-size: ${props => props.theme.fontSizes.lg};
`

const SubsectionTitle = styled.h3`
  margin: ${props => `0 ${props.theme.space.xs}`};
  padding: ${props => props.theme.space.xs};
  color: ${props => props.theme.colors.sidebar.subheading};
  font-weight: ${props => props.theme.fontWeights.semibold};
  text-transform: uppercase;
`

const StyledAccordion = styled(Accordion)`
  margin: ${props => props.theme.space.xs};
`

const StyledAccordionHeader = styled(Accordion.Header)`
  color: ${props => props.theme.colors.sidebar.foreground};
  font-weight: ${props => props.theme.fontWeights.regular};
  border-radius: ${props => props.theme.borderRadii.md};

  &:hover,
  &:active {
    color: ${props => props.theme.colors.sidebar.foreground};
    background-color: ${props =>
      props.theme.colors.sidebar.linkBackgroundHover};
  }
`

const CollapsibleSubsectionTitle = styled(Accordion.Label)`
  padding: ${props => props.theme.space.xs};
  color: inherit;
  font-weight: inherit;

  &:hover,
  &:active,
  &:focus {
    color: inherit;
  }
`

const StyledAccordionPanel = styled(Accordion.Panel)`
  padding-top: inherit;
  padding-right: 0;
  padding-bottom: inherit;
`

const SectionList = styled.ul`
  margin: ${props =>
    props.$isOuterList
      ? `${props.theme.space.horz.sm} 0`
      : `${props.theme.space.lg} 0`};
`

const SidebarSectionHeader = ({ data, ariaLabelId, sectionDepth }) => {
  const { title, link } = data
  if (sectionDepth === 0) {
    return <ParentSectionTitle id={ariaLabelId}>{title}</ParentSectionTitle>
  }
  if (sectionDepth === 1) {
    return (
      <SubsectionTitle id={ariaLabelId} key={link}>
        {title}
      </SubsectionTitle>
    )
  }
  return <CollapsibleSubsectionTitle>{title}</CollapsibleSubsectionTitle>
}

SidebarSectionHeader.propTypes = {
  data: sidebarDataNodeShape.isRequired,
  ariaLabelId: PropTypes.string.isRequired,
  sectionDepth: PropTypes.number
}

SidebarSectionHeader.defaultProps = {
  sectionDepth: 0
}

const SidebarSectionLinks = ({
  data,
  currentPage,
  sectionDepth,
  isTopLevel,
  isParentExpanded
}) => {
  if (data?.items === null) {
    return null
  }

  const { sidebarContentsVisible } = useContext(GlobalContext)

  return data?.items?.map((item, index) =>
    item.items ? (
      <SidebarLinkList
        key={index} // eslint-disable-line react/no-array-index-key
        data={item}
        currentPage={currentPage}
        sectionDepth={sectionDepth + 1}
        isTopLevel={isTopLevel}
      />
    ) : (
      <SidebarListItem
        key={index} // eslint-disable-line react/no-array-index-key
        item={item}
        currentPage={currentPage}
        isNested={sectionDepth >= 2}
        isTopLevel={isTopLevel}
        isFocusable={sidebarContentsVisible.get && isParentExpanded}
      />
    )
  )
}

SidebarSectionHeader.propTypes = {
  ariaLabelId: PropTypes.string.isRequired,
  sectionDepth: PropTypes.number,
  data: sidebarDataNodeShape.isRequired
}

SidebarSectionHeader.defaultProps = {
  sectionDepth: 0
}

SidebarSectionLinks.propTypes = {
  data: PropTypes.shape({
    items: PropTypes.arrayOf(sidebarDataNodeShape)
  }),
  currentPage: PropTypes.string,
  sectionDepth: PropTypes.number.isRequired,
  isTopLevel: PropTypes.bool,
  isParentExpanded: PropTypes.bool
}

SidebarSectionLinks.defaultProps = {
  data: {
    items: []
  },
  currentPage: "",
  isTopLevel: false,
  isParentExpanded: false
}

const SidebarLinkList = ({ data, sectionDepth, currentPage, isTopLevel }) => {
  if (!data) {
    return <li />
  }
  const sectionPath = data.link
  const isCurrentPageInSection =
    sectionDepth >= 2 && currentPage.includes(`/${sectionPath}`)
  const [expanded, setExpanded] = useState(isCurrentPageInSection)

  const sectionAriaLabelId = data?.link.replace(/\//g, "-")
  const sectionHeader = data.title && (
    <SidebarSectionHeader
      data={data}
      ariaLabelId={sectionAriaLabelId}
      sectionDepth={sectionDepth}
      currentPage={currentPage}
      isTopLevel={isTopLevel}
    />
  )

  const sectionLinks = (
    <SidebarSectionLinks
      data={data}
      currentPage={currentPage}
      sectionDepth={sectionDepth}
      isTopLevel={isTopLevel}
      isParentExpanded={sectionDepth < 2 || expanded}
    />
  )

  const sidebarSection =
    sectionDepth >= 2 ? (
      // ensure the aria-level of any label at this level of the navigation is higher than 2
      <StyledAccordion
        level={sectionDepth + 2}
        isBare
        isCompact
        expandedSections={expanded ? [0] : []}
        onFocus={() => !expanded && setExpanded(true)}
      >
        <Accordion.Section>
          <StyledAccordionHeader
            onMouseDown={e => {
              e.stopPropagation()
              return setExpanded(!expanded)
            }}
            onKeyDown={e => e.keyCode === 13 && setExpanded(!expanded)}
          >
            {sectionHeader}
          </StyledAccordionHeader>
          <StyledAccordionPanel>
            <ul>{sectionLinks}</ul>
          </StyledAccordionPanel>
        </Accordion.Section>
      </StyledAccordion>
    ) : (
      <SectionList $isOuterList={sectionDepth === 0}>
        <li>{sectionHeader}</li>
        {sectionLinks}
      </SectionList>
    )

  return <li>{sidebarSection}</li>
}

SidebarLinkList.propTypes = {
  data: sidebarDataNodeShape.isRequired,
  sectionDepth: PropTypes.number,
  currentPage: PropTypes.string,
  isTopLevel: PropTypes.bool
}

SidebarLinkList.defaultProps = {
  sectionDepth: 0,
  currentPage: null,
  isTopLevel: false
}

export default SidebarLinkList
